var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { DocumentRetrievalTaskSource } from "healthex-common/document.retrieval.dtos";
import { computed, observable, runInAction } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
const t = getObservableT("patients");
export class DocumentRetrievalSpecificLocationLoginViewModel {
    constructor(parentModel) {
        this.footerText = NOT_LOADED;
        this.sharingOrgsDialogOpen = false;
        this.parentModel = parentModel;
    }
    run() {
        void this.loadSharedOrgNames();
    }
    async loadSharedOrgNames() {
        this.footerText = await this.parentModel.loadSharedOrgNamesFooterText();
    }
    showSharingOrgsDialog() {
        this.sharingOrgsDialogOpen = true;
    }
    hideSharingOrgsDialog() {
        this.sharingOrgsDialogOpen = false;
    }
    stop() {
    }
    get loginLocation() {
        return this.parentModel.liveSpecificLocation;
    }
    get locationName() {
        var _a, _b, _c, _d;
        return (_d = (_b = (_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : (_c = this.parentModel.specificLocationResult) === null || _c === void 0 ? void 0 : _c.matchedLocation.organizationName) !== null && _d !== void 0 ? _d : "";
    }
    get locationLogoUrl() {
        var _a, _b, _c, _d;
        return (_d = (_b = (_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.logoUrl) !== null && _b !== void 0 ? _b : (_c = this.parentModel.specificLocationResult) === null || _c === void 0 ? void 0 : _c.matchedLocation.organizationLogoUrl) !== null && _d !== void 0 ? _d : null;
    }
    get locationSource() {
        var _a;
        return (_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.source;
    }
    get isParticleLocation() {
        return (this.locationSource === DocumentRetrievalTaskSource.PARTICLE_EPIC ||
            this.locationSource === DocumentRetrievalTaskSource.PARTICLE_FASTEN);
    }
    get isInsufficientScopeError() {
        return this.hasLoginError && this.parentModel.isInsufficientScopeError;
    }
    get isWaitingForAuth() {
        return !!this.loginLocation && this.loginLocation.key === this.parentModel.authInProgress;
    }
    get isLoginSuccessful() {
        return this.parentModel.isSpecificLocationSuccessState;
    }
    get isRefreshSuccessful() {
        return this.parentModel.isSpecificLocationRefreshSuccess;
    }
    get hasLoginError() {
        var _a;
        return !!this.parentModel.authErrorKey && ((_a = this.loginLocation) === null || _a === void 0 ? void 0 : _a.key) === this.parentModel.authErrorKey;
    }
    get loginErrorMessage() {
        if (!this.hasLoginError)
            return null;
        return this.parentModel.authErrorMessage;
    }
    get isAlreadyAdded() {
        return this.parentModel.isShowingSpecificLocationAlreadyAdded;
    }
    get isRefreshFlow() {
        return this.parentModel.isRefresh;
    }
    get providerName() {
        return this.locationName;
    }
    get addedDateLabel() {
        const addedAt = this.parentModel.specificLocationAddedAt;
        if (!addedAt) {
            return undefined;
        }
        const parsed = new Date(addedAt);
        if (Number.isNaN(parsed.getTime())) {
            return undefined;
        }
        return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
    get title() {
        if (this.isAlreadyAdded && this.isRefreshFlow)
            return t("Refresh records");
        if (this.isAlreadyAdded)
            return t("Already added");
        if (this.isInsufficientScopeError)
            return t("Missing MyChart Permission");
        if (this.isWaitingForAuth)
            return t("Logging in...");
        if (this.isLoginSuccessful) {
            return this.isRefreshSuccessful ? t("Refresh Successful") : t("Log In Successful");
        }
        return t("Log in required");
    }
    get refreshButtonText() {
        return t("Refresh these records");
    }
    get keepCurrentRecordsButtonText() {
        return t("Keep current records");
    }
    get supportingText() {
        if (this.isWaitingForAuth) {
            return t("If you’re having trouble or can’t remember your credentials, you can close this and try again later.");
        }
        if (this.isLoginSuccessful) {
            return this.parentModel.isHealthWalletEnabled
                ? t("These records will be added to your HealthEx Wallet.")
                : t("We’ll bring these records into your HealthEx Unified Record.");
        }
        return t("Log in with your patient portal credentials to add this provider.");
    }
    get loginButtonText() {
        if (this.hasLoginError)
            return t("Try again");
        if (this.isWaitingForAuth)
            return t("Logging in...");
        return t("Log in");
    }
    get completeButtonText() {
        if (!this.parentModel.canRedirectExternally) {
            return t("Continue");
        }
        const label = this.parentModel.returnToPartnerLabel;
        if (label)
            return label;
        if (this.parentModel.isAIFlow) {
            return t("Finish and return to {{ aiToolName }}", { aiToolName: this.aiToolName });
        }
        return this.parentModel.hostedFlowButtonText;
    }
    get exitButtonText() {
        if (!this.parentModel.canRedirectExternally) {
            return this.parentModel.healthWalletExitButtonText;
        }
        if (this.parentModel.isAIFlow) {
            return t("Go back to {{ aiToolName }}", { aiToolName: this.aiToolName });
        }
        return this.parentModel.hostedFlowDefaultButtonText;
    }
    get aiToolName() {
        return this.parentModel.aiToolName;
    }
    get showAiCountdownTimer() {
        return this.parentModel.showSpecificLocationSuccessCountdown;
    }
    get aiCountdownSeconds() {
        return this.parentModel.specificLocationSuccessCountdownSeconds;
    }
    get maxAiCountdownSeconds() {
        return this.parentModel.maxSpecificLocationSuccessCountdownSeconds;
    }
    get isCompleting() {
        return this.parentModel.isCompletingSpecificLocationFlow;
    }
    get isRefreshing() {
        return this.isCompleting || this.isWaitingForAuth;
    }
    async handleLogin() {
        await this.parentModel.loginToSpecificLocation();
    }
    async onComplete() {
        await this.parentModel.completeSpecificLocationSuccessFlow();
    }
    async onRefresh() {
        await this.parentModel.refreshSpecificLocation();
    }
    async onExit() {
        if (this.isCompleting)
            return;
        runInAction(() => {
            this.parentModel.isCompletingSpecificLocationFlow = true;
        });
        try {
            await this.parentModel.exitSpecificLocationFlow();
        }
        finally {
            runInAction(() => {
                this.parentModel.isCompletingSpecificLocationFlow = false;
            });
        }
    }
}
__decorate([
    observable
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "footerText", void 0);
__decorate([
    observable
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "sharingOrgsDialogOpen", void 0);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "loginLocation", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "locationName", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "locationLogoUrl", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "locationSource", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "isParticleLocation", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "isInsufficientScopeError", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "isWaitingForAuth", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "isLoginSuccessful", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "isRefreshSuccessful", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "hasLoginError", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "loginErrorMessage", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "isAlreadyAdded", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "isRefreshFlow", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "providerName", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "addedDateLabel", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "title", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "refreshButtonText", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "keepCurrentRecordsButtonText", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "supportingText", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "loginButtonText", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "completeButtonText", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "exitButtonText", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "aiToolName", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "showAiCountdownTimer", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "aiCountdownSeconds", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "maxAiCountdownSeconds", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "isCompleting", null);
__decorate([
    computed
], DocumentRetrievalSpecificLocationLoginViewModel.prototype, "isRefreshing", null);
